import { useTranslation } from "react-i18next";
import { getUserSpecialisationName } from "src/hooks/useUser";

import isPes from "../../../../helpers/isPes";
import {
	AdditionalOptionsFilter,
	DepartmentsFilter,
	ExamTypeFilter,
	TermsFilter,
} from "../../Filters";
import CemVersionsFilter from "../../Filters/CemVersions";
import ConsultantsOptionsFilter from "../../Filters/ConsultantsOptionsFilter";
import QuestionLevelsFilter from "../../Filters/QuestionLevelsFilter";
import Filters from "./index";

export default function ExamFilters({
	examDetails,
	setData,
	departments,
	terms,
	isLoading,
	withClearAll = false,
	allowList = null,
	renderExtraFilters,
	renderExtraFiltersBefore = undefined,
	cemVersions,
	initialState = undefined,
	count = undefined,
}) {
	const { t } = useTranslation();
	const setSelectedData = (newData) =>
		setData({
			...examDetails,
			...newData,
			questions: [],
		});

	const getExamTypeHint = () => {
		if (isPes()) {
			return t("filters.exams.typeOfExam.pes.hint");
		}

		return t("filters.exams.typeOfExam.lek.hint");
	};

	const renderTypeOfExam = (expanded, handleChange) => {
		if (allowList && !allowList.includes("examType")) {
			return null;
		}

		return (
			<ExamTypeFilter
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				selected={examDetails.typeOfExam !== null}
				expanded={expanded === 0}
				onChange={() => handleChange(0)}
				hint={getExamTypeHint()}
				cemVersions={cemVersions}
			/>
		);
	};

	const { starred, wrongAnswers, skipCanceledQuestions, isNote, isUnanswered } =
		examDetails.additionalOption;
	const isExamTypeFilterSelected = examDetails.typeOfExam !== null;
	const cemVersionSelected = examDetails.cemVersions.length > 0;
	const isSomeDepartmentSelected =
		examDetails.departments.length > 0 ||
		examDetails.tags.length > 0 ||
		examDetails.subtags.length > 0;
	const isSomeTermSelected = examDetails.terms.length > 0;
	const isSomeConsultantsSelected = examDetails.consultants.length > 0;
	const isSomeLevelSelected = examDetails.level.length > 0;
	const isSomeAdditionalOptionSelected =
		starred ||
		wrongAnswers ||
		skipCanceledQuestions ||
		isNote ||
		isUnanswered ||
		examDetails.shuffle?.checked;

	const calculateSelectedFilters = () => {
		return (
			+isSomeDepartmentSelected +
			+isSomeTermSelected +
			+!!isSomeAdditionalOptionSelected +
			+!!isSomeConsultantsSelected +
			+isExamTypeFilterSelected +
			+cemVersionSelected +
			+isSomeLevelSelected
		);
	};

	const clearAll = () => {
		return setSelectedData(initialState);
	};

	const getDepartmentsHint = () => {
		if (isPes()) {
			return t("filters.exams.departments.pesHint", {
				specialisation: getUserSpecialisationName(),
			});
		}

		return t("filters.exams.departments.lekHint");
	};

	const renderDepartmentsFilter = (expanded, handleChange) => {
		if (allowList && !allowList.includes("departments")) {
			return null;
		}

		return (
			<DepartmentsFilter
				data-testid="departmentsFilter"
				selected={isSomeDepartmentSelected}
				departments={departments}
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === 2}
				onChange={() => handleChange(2)}
				hint={getDepartmentsHint()}
			/>
		);
	};

	const renderTerms = (expanded, handleChange) => {
		if (allowList && !allowList.includes("terms")) {
			return null;
		}

		return (
			<TermsFilter
				selected={isSomeTermSelected}
				terms={terms}
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === 3}
				onChange={() => handleChange(3)}
			/>
		);
	};

	const renderConsultantsOptions = (expanded, handleChange) => {
		if (allowList && !allowList.includes("consultants")) {
			return null;
		}

		if (!isPes()) {
			return null;
		}

		return (
			<ConsultantsOptionsFilter
				selected={isSomeConsultantsSelected}
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === 4}
				onChange={() => handleChange(4)}
			/>
		);
	};

	const renderAdditionalOptions = (expanded, handleChange) => {
		if (allowList && !allowList.includes("additionalOptions")) {
			return null;
		}

		return (
			<AdditionalOptionsFilter
				count={count}
				selected={isSomeAdditionalOptionSelected}
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === 5}
				onChange={() => handleChange(5)}
			/>
		);
	};

	const renderQuestionLevelFilters = (expanded, handleChange) => {
		if (allowList && !allowList.includes("questionLevel")) {
			return null;
		}

		return (
			<QuestionLevelsFilter
				selected={false}
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === 8}
				onChange={() => handleChange(8)}
			/>
		);
	};

	const renderCemVersionFilter = (expanded, handleChange) => {
		if (
			!cemVersions ||
			examDetails.typeOfExam !== "cem" ||
			(Array.isArray(cemVersions) && cemVersions.length <= 1)
		) {
			return null;
		}

		return (
			<CemVersionsFilter
				cemVersions={cemVersions}
				selected={examDetails.cemVersions.length > 0}
				selectedData={examDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === 1}
				onChange={() => handleChange(1)}
			/>
		);
	};

	const renderFilters = (expanded, handleChange) => {
		return (
			<div className="flex flex-col gap-12">
				{renderExtraFiltersBefore?.(expanded, handleChange)}
				{renderTypeOfExam(expanded, handleChange)}
				{renderCemVersionFilter(expanded, handleChange)}
				{renderDepartmentsFilter(expanded, handleChange)}
				{renderTerms(expanded, handleChange)}
				{renderConsultantsOptions(expanded, handleChange)}
				{renderQuestionLevelFilters(expanded, handleChange)}
				{renderAdditionalOptions(expanded, handleChange)}
				{renderExtraFilters?.(expanded, handleChange)}
			</div>
		);
	};

	return (
		<Filters
			renderFilters={renderFilters}
			isLoading={isLoading}
			filtersCount={!!withClearAll && calculateSelectedFilters()}
			clearAll={clearAll}
		/>
	);
}
